import { useState, useEffect, useCallback, useRef } from 'react';
import { UpdStates } from '../../../FermaHelper';
import { useTimer } from './useTimer';
import { get, getText } from '../../../../components/hooks/apiFetch';

export const useProcessingStatus = (
    utilityName,
    inputIdentifier,
    initialFileNames = null,   // массив или null
    initialId = null,          // число/строка или null
    initialComment = null) => {
    const MAX_RETRIES = 5;
    const retryCountRef = useRef(0);
    const [state, setState] = useState({
        // Access state
        hasAccess: false,
        isCheckingAccess: true,
        accessError: null,

        // Id state
        id: initialId,
        isLoadingId: !(initialId != null),
        idError: null,

        // File names state
        fileNames: initialFileNames || [],
        isLoadingFileNames: !(initialFileNames != null),
        fileNamesError: null,

        // Comment state
        comment: initialComment,
        isLoadingComment: !(initialComment != null),
        commentError: null,

        // Status state
        statusData: null,
        statusError: null,
        isStatusLoading: true,
        lastUpdate: null
    });

    const timer = useTimer(false, 0);
    const timerRef = useRef(timer);
    timerRef.current = timer;

    const isMountedRef = useRef(true);
    const pollTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    const checkTaskPermission = useCallback(async (inputIdentifier) => {
        if (!inputIdentifier) return false;

        try {
            await get(`/api/TaskFileAccess/CheckTaskPermission/${encodeURIComponent(inputIdentifier)}`);
            return true;
        } catch (err) {
            if (err.status === 403) {
                return false;
            }
            console.error('Error checking task permission:', err);
            throw err;
        }
    }, []);

    const fetchId = useCallback(async (inputIdentifier) => {
        try {
            const id = await get(`/api/OperationInfo/GetInputId/${encodeURIComponent(inputIdentifier)}`);
            return id;
        } catch (error) {
            console.error('Failed to fetch file names:', error);
            throw error;
        }
    }, []);

    const fetchFileNames = useCallback(async (inputIdentifier) => {
        try {
            const fileNames = await get(`/api/OperationInfo/GetOriginalFileNames/${encodeURIComponent(inputIdentifier)}`);
            return Array.isArray(fileNames) ? fileNames : [];
        } catch (error) {
            console.error('Failed to fetch file names:', error);
            throw error;
        }
    }, []);

    const fetchComment = useCallback(async (inputIdentifier) => {
        try {
            const comment = await getText(`/api/OperationInfo/GetComment/${encodeURIComponent(inputIdentifier)}`);
            return comment;
        } catch (error) {
            console.error('Failed to fetch file names:', error);
            throw error;
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const data = await get(`/api/${encodeURIComponent(utilityName)}/${encodeURIComponent(inputIdentifier)}`, {
                signal: controller.signal,
            });

            if (!isMountedRef.current) return null;

            const serverElapsedTime = data?.durationSeconds || 0;
            const currentStatus = data?.status;

            const isActive =
                currentStatus &&
                UpdStates.includes(currentStatus);

            const currentTimer = timerRef.current;

            currentTimer.syncWithServerTime(serverElapsedTime);

            if (isActive && !currentTimer.isActive) {
                currentTimer.startTimer();
            }

            if (!isActive && currentTimer.isActive) {
                currentTimer.stopTimer();
            }

            setState(prev => ({
                ...prev,
                statusData: data,
                statusError: null,
                lastUpdate: new Date(),
                isStatusLoading: false,
            }));
            return data;
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }

            if (error.status === 403) {
                window.location.href = '/no-access';
                return null;
            }

            const errorMessage = error.message || 'Ошибка загрузки статуса обработки';
            if (isMountedRef.current) {
                setState((prev) => ({
                    ...prev,
                    statusError: errorMessage,
                    isStatusLoading: false,
                }));
            }
            console.error('Error fetching status:', error);
            throw error;
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
        }
    }, [utilityName, inputIdentifier]);

    const checkAccess = useCallback(async () => {
        if (!inputIdentifier) {
            setState(prev => ({ ...prev, hasAccess: false, isCheckingAccess: false }));
            return false;
        }
        try {
            setState(prev => ({ ...prev, isCheckingAccess: true, accessError: null }));
            const hasPermission = await checkTaskPermission(inputIdentifier);
            if (isMountedRef.current) {
                setState((prev) => ({
                    ...prev,
                    hasAccess: hasPermission,
                    isCheckingAccess: false,
                }));
            }

            if (!hasPermission) {
                window.location.href = '/no-access';
                return false;
            }

            return hasPermission;
        } catch (err) {
            setState(prev => ({ ...prev, accessError: err.message, hasAccess: false, isCheckingAccess: false }));
            return false;
        }
    }, [inputIdentifier, checkTaskPermission]);

    const loadMissingData = useCallback(async () => {
        if (!inputIdentifier) return;

        // Загружаем ID, если не передан начальный
        if (initialId === null) {
            setState((prev) => ({ ...prev, isLoadingId: true, idError: null }));
            try {
                const id = await fetchId(inputIdentifier);
                if (isMountedRef.current) {
                    setState((prev) => ({ ...prev, id, isLoadingId: false }));
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setState((prev) => ({ ...prev, idError: error.message, isLoadingId: false }));
                }
            }
        }

        // Загружаем имена файлов, если не переданы
        if (initialFileNames === null) {
            setState((prev) => ({ ...prev, isLoadingFileNames: true, fileNamesError: null }));
            try {
                const names = await fetchFileNames(inputIdentifier);
                if (isMountedRef.current) {
                    setState((prev) => ({ ...prev, fileNames: names, isLoadingFileNames: false }));
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setState((prev) => ({ ...prev, fileNamesError: error.message, isLoadingFileNames: false }));
                }
            }
        }

        // Загружаем комментарий, если не передан
        if (initialComment === null) {
            setState((prev) => ({ ...prev, isLoadingComment: true, commentError: null }));
            try {
                const comment = await fetchComment(inputIdentifier);
                if (isMountedRef.current) {
                    setState((prev) => ({ ...prev, comment, isLoadingComment: false }));
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setState((prev) => ({ ...prev, commentError: error.message, isLoadingComment: false }));
                }
            }
        }
    }, [inputIdentifier, initialId, initialFileNames, initialComment, fetchId, fetchFileNames, fetchComment]);

    const startPolling = useCallback(async () => {
        // Очищаем предыдущий таймер
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }

        const poll = async () => {
            if (!isMountedRef.current) return;

            try {
                const data = await fetchStatus();
                if (!isMountedRef.current) return;

                // При успешном ответе сбрасываем счётчик ошибок
                retryCountRef.current = 0;

                const isActive = data?.status && UpdStates.includes(data.status);
                if (isActive) {
                    // Продолжаем опрос, если статус активен
                    pollTimeoutRef.current = setTimeout(poll, 5000);
                }
                // Если статус завершён – опрос останавливается (не запускаем новый таймер)
            } catch (error) {
                if (!isMountedRef.current) return;

                // Если ошибка критична (ресурс не найден, нет прав) – прекращаем опрос
                if (error.status === 404 || error.status === 403) {
                    setState((prev) => ({
                        ...prev,
                        statusError: error.message || 'Ресурс недоступен',
                        isStatusLoading: false,
                    }));
                    return;
                }

                // Увеличиваем счётчик неудачных попыток
                retryCountRef.current += 1;

                if (retryCountRef.current >= MAX_RETRIES) {
                    // Превышено число попыток – останавливаем опрос и показываем ошибку
                    setState((prev) => ({
                        ...prev,
                        statusError: `Не удалось получить статус после ${MAX_RETRIES} попыток. ${error.message || ''}`,
                        isStatusLoading: false,
                    }));
                    return;
                }

                // Повторяем попытку через 5 секунд (с экспоненциальной задержкой – опционально)
                const delay = Math.min(5000 * Math.pow(1.5, retryCountRef.current - 1), 30000);
                pollTimeoutRef.current = setTimeout(poll, delay);
            }
        };

        // Запускаем первый опрос
        poll();
    }, [fetchStatus]);

    useEffect(() => {
        isMountedRef.current = true;

        const init = async () => {
            // Если нет идентификатора – ничего не делаем
            if (!inputIdentifier) {
                setState((prev) => ({ ...prev, isStatusLoading: false }));
                return;
            }

            // 1. Проверяем доступ
            const hasAccess = await checkAccess();
            if (!hasAccess || !isMountedRef.current) return;

            // 2. Загружаем недостающие данные
            await loadMissingData();
            if (!isMountedRef.current) return;

            // 3. Запускаем опрос статуса
            startPolling();
        };

        init();

        // Очистка при размонтировании
        return () => {
            isMountedRef.current = false;
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            // Останавливаем таймер, если он активен
            if (timerRef.current.isActive) {
                timerRef.current.stopTimer();
            }
        };
        // Зависимости: перезапускаем, если меняется идентификатор или начальные пропсы
    }, [inputIdentifier, initialId, initialFileNames, initialComment, checkAccess, loadMissingData, startPolling]);

    const refreshStatus = useCallback(async () => {
        return await fetchStatus();
    }, [fetchStatus]);

    const refreshAccess = useCallback(async () => {
        return await checkAccess();
    }, [checkAccess]);

    const refreshFileNames = useCallback(async () => {
        if (!inputIdentifier) return [];
        setState((prev) => ({ ...prev, isLoadingFileNames: true, fileNamesError: null }));
        try {
            const names = await fetchFileNames(inputIdentifier);
            setState((prev) => ({ ...prev, fileNames: names, isLoadingFileNames: false }));
            return names;
        } catch (error) {
            setState((prev) => ({ ...prev, fileNamesError: error.message, isLoadingFileNames: false }));
            throw error;
        }
    }, [inputIdentifier, fetchFileNames]);

    const refreshId = useCallback(async () => {
        if (!inputIdentifier) return null;
        setState((prev) => ({ ...prev, isLoadingId: true, idError: null }));
        try {
            const id = await fetchId(inputIdentifier);
            setState((prev) => ({ ...prev, id, isLoadingId: false }));
            return id;
        } catch (error) {
            setState((prev) => ({ ...prev, idError: error.message, isLoadingId: false }));
            throw error;
        }
    }, [inputIdentifier, fetchId]);

    const refreshComment = useCallback(async () => {
        if (!inputIdentifier) return null;
        setState((prev) => ({ ...prev, isLoadingComment: true, commentError: null }));
        try {
            const comment = await fetchComment(inputIdentifier);
            setState((prev) => ({ ...prev, comment, isLoadingComment: false }));
            return comment;
        } catch (error) {
            setState((prev) => ({ ...prev, commentError: error.message, isLoadingComment: false }));
            throw error;
        }
    }, [inputIdentifier, fetchComment]);

    const verifyAccess = useCallback(async () => {
        if (!inputIdentifier) return false;
        return await checkTaskPermission(inputIdentifier);
    }, [inputIdentifier, checkTaskPermission]);

    return {
        // Доступ
        hasAccess: state.hasAccess,
        isCheckingAccess: state.isCheckingAccess,
        accessError: state.accessError,

        // ID
        id: state.id,
        isLoadingId: state.isLoadingId,
        idError: state.idError,

        // Имена файлов
        fileNames: state.fileNames,
        isLoadingFileNames: state.isLoadingFileNames,
        fileNamesError: state.fileNamesError,

        // Комментарий
        comment: state.comment,
        isLoadingComment: state.isLoadingComment,
        commentError: state.commentError,

        // Статус
        data: state.statusData,
        error: state.statusError,
        isProcessing: state.statusData?.status && UpdStates.includes(state.statusData.status),
        isStatusLoading: state.isStatusLoading,
        lastUpdate: state.lastUpdate,

        // Время (из хука useTimer)
        timerElapsedTime: timer.elapsedTime,

        // Методы
        fetchStatus: refreshStatus,
        checkAccess: refreshAccess,
        verifyAccess,
        refreshFileNames,
        refreshId,
        refreshComment,
    };
};